import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AuthorizationRequest } from '@/lib/authorization/types';
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

export type QueueFilter = 
  | { type: 'all' }
  | { type: 'payer'; payerId: string }
  | { type: 'surgeon'; surgeonId: string }
  | { type: 'facility'; facilityId: string }
  | { type: 'surgery_date'; startDate: string; endDate?: string }
  | { type: 'sla_urgency' };

interface UseOpsQueueOptions {
  filter?: QueueFilter;
  statusFilter?: string[];
}

export function useOpsQueue({ filter = { type: 'all' }, statusFilter }: UseOpsQueueOptions = {}) {
  const [cases, setCases] = useState<AuthorizationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCases = useCallback(async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('authorization_requests')
        .select(`
          *,
          patient:patients(*),
          payer:payers(*),
          facility:facilities(*)
        `)
        .order('created_at', { ascending: false });

      // Apply status filter
      if (statusFilter && statusFilter.length > 0) {
        query = query.in('status', statusFilter as AuthorizationRequest['status'][]);
      }

      // Apply queue filter
      switch (filter.type) {
        case 'payer':
          query = query.eq('payer_id', filter.payerId);
          break;
        case 'surgeon':
          query = query.eq('surgeon_id', filter.surgeonId);
          break;
        case 'facility':
          query = query.eq('facility_id', filter.facilityId);
          break;
        case 'surgery_date':
          query = query.gte('scheduled_date', filter.startDate);
          if (filter.endDate) {
            query = query.lte('scheduled_date', filter.endDate);
          }
          break;
        case 'sla_urgency':
          // Order by SLA deadline (closest first)
          query = query
            .not('sla_deadline', 'is', null)
            .order('sla_deadline', { ascending: true });
          break;
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setCases((data as unknown as AuthorizationRequest[]) || []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // Subscribe to realtime updates
  useEffect(() => {
    const channel = supabase
      .channel('ops-queue-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'authorization_requests',
        },
        (payload: RealtimePostgresChangesPayload<AuthorizationRequest>) => {
          if (payload.eventType === 'INSERT') {
            setCases(prev => [payload.new as AuthorizationRequest, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setCases(prev => 
              prev.map(c => c.id === (payload.new as AuthorizationRequest).id ? { ...c, ...payload.new } : c)
            );
          } else if (payload.eventType === 'DELETE') {
            setCases(prev => prev.filter(c => c.id !== (payload.old as { id: string }).id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    cases,
    loading,
    error,
    refetch: fetchCases,
  };
}
